<template>
  <div class="file-upload-popup">
    <!-- 预览区（有文件时显示） -->
    <div class="preview" v-if="previewUrl">
      <video v-if="fileCategory === 'video'" :src="previewUrl"
        controls autoplay loop muted />
      <AudioPlayer v-else-if="fileCategory === 'audio'" :url="previewUrl" />
      <el-image v-else-if="fileCategory === 'image'" :src="previewUrl" fit="contain"
        :preview-src-list="[previewUrl]" preview-teleported />
      <div v-else-if="fileCategory === 'pdf'" style="position:relative; width: 12.5rem; height: 7.5rem; cursor: pointer; overflow: hidden;" @click="openFullPreview">
        <iframe v-if="pdfBlobUrl" :src="pdfBlobUrl" class="pdf-thumb" title="PDF预览" style="width: 100%; height: 100%;" scrolling="no" />
        <div v-else class="doc-preview"><svg-icon iconName="loading" /></div>
        <div style="position: absolute; top:0; left:0; right:0; bottom:0;"></div>
      </div>
      <div v-else class="doc-preview" style="cursor: pointer;" @click="openFullPreview">
        <svg-icon :iconName="docIconName" />
        <span class="doc-name ellipsis">{{ fileName }}</span>
      </div>
    </div>

    <!-- 首次上传（无文件时显示大图标） -->
    <el-upload v-if="!previewUrl && !loading"
      :show-file-list="false"
      :auto-upload="false"
      :multiple="false"
      action=""
      @change="handleFileChange"
      style="margin: 1.25rem 0;"
    >
      <svg-icon iconName="file-upload" fillColor="var(--themeColor, rgb(81, 177, 217))" style="width: 3rem; height: 3rem; cursor: pointer;" />
    </el-upload>

    <svg-icon v-if="loading && !previewUrl" iconName="loading" fillColor="var(--themeColor, rgb(81, 177, 217))" style="margin: 1.25rem 0;" />

    <!-- 操作按钮（始终显示） -->
    <div class="actions">
      <el-upload
        :show-file-list="false"
        :auto-upload="false"
        :multiple="false"
        action=""
        @change="handleFileChange"
      >
        <el-button size="small" type="primary" :loading="loading">选择文件</el-button>
      </el-upload>
      <el-button size="small" type="primary" :loading="loading"
        @click="handleSave">上传保存</el-button>
    </div>

    <!-- 全屏预览弹窗（PDF / Word）teleport 到 body -->
    <teleport to="body">
      <el-dialog v-model="fullPreviewVisible" fullscreen destroy-on-close :title="fileName">
        <iframe v-if="fileCategory === 'pdf' && pdfBlobUrl" :src="pdfBlobUrl"
          style="width:100%;height:calc(100vh - 6.25rem);border:none;" />
        <div v-else-if="fileCategory === 'word'" ref="wordContainerRef" style="height:calc(100vh - 6.25rem);overflow-y:auto;background:#eaebf0;padding:1.25rem 0;"></div>
      </el-dialog>
    </teleport>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import store from '@/store';
import { message } from '@/utils/js/initVariable';
import { getFileCategory } from '@/utils/js/file';
import SvgIcon from '@/components/SvgIcon';
import AudioPlayer from '@/components/AudioPlayer';
import { renderAsync } from 'docx-preview';
import axios from 'axios';

import { uploadShootingLocationVideo, getAuthLink } from '@/api/UserWorkspaceDataApi';
import {
  addShootingLocation,
} from '@/api/ShootingLocationApi';

// props
const props = defineProps({
  lat: { type: Number, required: true },
  lng: { type: Number, required: true },
  // 回显用（编辑已有数据时）
  initialData: { type: Object, default: undefined },
});

const loading = ref(false);
const editId = ref(props.initialData?.id || undefined);
const fileName = ref(props.initialData?.videoFileName || '');
const previewUrl = ref(undefined);
const fileCategory = ref('unknown');

// 文档类型图标映射
const docIconMap = {
  'document': 'file',
  'pdf': 'file-pdf-box',
  'word': 'file-word-box',
  'unknown': 'file',
};
const docIconName = computed(() => docIconMap[fileCategory.value] || 'file');

const fullPreviewVisible = ref(false);
const wordContainerRef = ref(null);
const pdfBlobUrl = ref('');

// 监听预览链接，如果是PDF，则转换为Blob URL以防止被浏览器直接下载
watch(() => previewUrl.value, async (newUrl) => {
  if (newUrl && fileCategory.value === 'pdf') {
    try {
      const res = await axios.get(newUrl, { responseType: 'blob' });
      // 强制明确 MimeType，防止其响应为 application/octet-stream 时还是被下载
      const blob = new Blob([res.data], { type: 'application/pdf' });
      if (pdfBlobUrl.value) {
        URL.revokeObjectURL(pdfBlobUrl.value.split('#')[0]); // revoke 时去掉 hash 参数
      }
      // 追加 #scrollbar=0&toolbar=0 防止缩略图出现滚动条
      pdfBlobUrl.value = URL.createObjectURL(blob) + '#view=Fit&scrollbar=0&toolbar=0&navpanes=0';
    } catch (err) {
      console.error('Failed to load PDF blob:', err);
    }
  } else {
    if (pdfBlobUrl.value) {
      URL.revokeObjectURL(pdfBlobUrl.value.split('#')[0]);
      pdfBlobUrl.value = '';
    }
  }
}, { immediate: true });

const openFullPreview = () => {
  if (['video', 'audio', 'image'].includes(fileCategory.value)) return;
  if (fileCategory.value === 'document' || fileCategory.value === 'unknown') {
    // Other documents, download
    const link = document.createElement('a');
    link.href = previewUrl.value;
    link.download = fileName.value;
    link.target = '_blank';
    link.click();
    return;
  }
  
  fullPreviewVisible.value = true;
  if (fileCategory.value === 'word') {
    renderWordPreview();
  }
}

const renderWordPreview = async () => {
  if (!previewUrl.value) return;
  try {
    const res = await axios.get(previewUrl.value, { responseType: 'blob' });
    if (wordContainerRef.value && res.data) {
      await renderAsync(res.data, wordContainerRef.value, null, {
        className: 'docx-preview-wrapper',
        inWrapper: true,
        ignoreFonts: false,
        breakPages: true,
      });
    }
  } catch (err) {
    message('文档加载失败', 2);
    console.error('Word rendering error', err);
  }
}

// 初始化回显
if (props.initialData?.videoFileName) {
  loading.value = true;
  fileName.value = props.initialData.videoFileName;
  fileCategory.value = getFileCategory(fileName.value);
  getAuthLink(props.initialData.videoFileName).then(res => {
    if (res.data.code === 1004) {
      previewUrl.value = res.data.data;
    }
    loading.value = false;
  }).catch(err => {
    loading.value = false;
    console.log(err);
  });
}

// 文件选择处理
const handleFileChange = (file) => {
  loading.value = true;
  fileName.value = file.name;
  fileCategory.value = getFileCategory(file.name);

  uploadShootingLocationVideo({
    file: file.raw,
  }).then(res => {
    if (res.data.code == 1004) {
      fileName.value = res.data.data['imageName'];
      previewUrl.value = res.data.data['data'];
      // 上传成功后根据服务器返回的文件名重新判断类型
      fileCategory.value = getFileCategory(fileName.value);
    }
    loading.value = false;
  }).catch(err => {
    loading.value = false;
    console.log(err);
  });
};

// 保存到后端
const handleSave = () => {
  if (!fileName.value) {
    message('请选择上传文件', 2);
    return;
  }
  loading.value = true;
  const mapId = store.state.workBenchMapId;
  addShootingLocation({
    id: editId.value,
    lat: props.lat,
    lon: props.lng,
    videoFileName: fileName.value,
    mapId,
  }).then(res => {
    if (res.data.code == 1004) {
      editId.value = res.data.data;
      message(res.data.msg || '保存成功', 1);
    }
    loading.value = false;
  }).catch(err => {
    loading.value = false;
    console.log(err);
  });
};
</script>

<style lang='less'></style>

<style lang='less' scoped>
.file-upload-popup {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;

  .preview {
    video {
      width: 200px;
      height: 120px;
      border-radius: 4px;
    }

    :deep(.el-image) {
      width: 200px;
      height: 120px;
      border-radius: 4px;
    }

    .pdf-thumb {
      width: 200px;
      height: 120px;
      border: none;
      border-radius: 4px;
      background-color: #fff;
      cursor: pointer;
      pointer-events: none; /* Make click pass through to parent div if wrapped, or trigger click manually */
    }

    .doc-preview {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 8px 12px;
      background-color: var(--backgroundColor_38);
      border-radius: var(--borderRadiusSize);
      min-width: 120px;
      transition: background-color 0.2s;

      &:hover {
        background-color: var(--backgroundColor_4);
      }

      .doc-name {
        max-width: 160px;
        font-size: 13px;
      }
    }
  }

  .actions {
    display: flex;
    gap: 5px;
    width: 100%;
    justify-content: center;

    :deep(.el-upload) {
      display: inline-block;
    }
  }
}
</style>
